import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./MusicCatalog.module.css";
import { useSelector } from "react-redux";
import MusicPlayer from "../MusicPlayer/MusicPlayer";
import ServiceSelector from "../MusicPlayer/ServiceSelector/ServiceSelector";
import RelatedListening from "./RelatedListening/RelatedListening";
import { useCatalogBrowseFilter } from "./CatalogBrowseFilterContext";
import { usePreviewStores } from "../../data/CmsPreviewContext";
import {
  catalogItemAnchorId,
  filterCatalogForBrowse,
  formatTypeLabel,
  getListenableCatalogItems,
  getMetadataValues,
  getRelatedItems,
  LISTENABLE_TYPES,
  normalizeTags,
  sortByReleaseNewest,
  sortCatalogItems,
} from "../../Hooks/catalogPresentation";
import SectionHeroTitle from "../SectionHeroTitle/SectionHeroTitle";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "title-asc", label: "Title A–Z" },
  { value: "title-desc", label: "Title Z–A" },
  { value: "type", label: "Type" },
];

// Re-export a local active check for featured flag (truthy string "true")
const isFeatured = (item) => {
  if (!item) return false;
  const v = item.isFeaturedPlaylist;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return Boolean(v);
};

/** Small/compact service iframe attrs apply only to songs in the browse grid. */
const isSong = (item) =>
  String(item?.type || "")
    .trim()
    .toLowerCase() === "song";

/**
 * Welcome-style player chassis (full panel, no diagonal cut).
 * Roll back: render children only (unwrap MusicPlayer from this shell).
 */
const PlayerChassis = ({ children }) => (
  <div className={styles["player-chassis"]}>
    <div className={styles["player-chassis-shell"]} aria-hidden="true" />
    <div className={styles["player-chassis-border"]} aria-hidden="true" />
    <div className={styles["player-chassis-edge"]} aria-hidden="true" />
    <div className={styles["player-viewport"]}>{children}</div>
  </div>
);

const CatalogPlayerCard = ({
  entry,
  relatedItems,
  featured = false,
  playlistSlugClass,
}) => {
  const anchorId = catalogItemAnchorId(entry);
  return (
    <li
      id={anchorId || undefined}
      className={
        styles["playlist"] +
        " " +
        (playlistSlugClass || "") +
        " " +
        styles[featured || isFeatured(entry) ? "featured" : "not-featured"] +
        " " +
        styles["catalog-card"]
      }
    >
      <PlayerChassis>
        <MusicPlayer
          title={entry.title}
          catalogItem={entry}
          sourceURLObj={entry.sourceURLObj}
          hideServiceSelector={true}
          showPlayerFrame1={true}
          isFeaturedPlaylist={isFeatured(entry)}
          // Full iframe attrs for featured + albums/playlists.
          // Small/compact Admin section only applies to songs in the browse grid.
          embedLayout={featured || !isSong(entry) ? "full" : "compact"}
          showNowPlayingHint={false}
          compactMeta={false}
          maxTags={2}
          showAbout={true}
          passedStyles={{
            boxShadow: "none",
            background: "transparent",
          }}
        />
        <RelatedListening items={relatedItems} seedTitle={entry.title} />
      </PlayerChassis>
    </li>
  );
};

const MusicCatalog = (props) => {
  // Can use https://www.browserling.com/tools/extract-urls
  // for extracting URL from iframe code (mostly works)

  const { welcomeScrollPosition } = useSelector(
    (state) => state.scrollPosition,
  );
  const catalogMetadata = useSelector(
    (state) => state.catalogData.catalogMetadata,
  );

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortMode, setSortMode] = useState("newest");
  const [vibeMenuOpen, setVibeMenuOpen] = useState(false);
  const vibeMenuRef = useRef(null);
  const {
    selectedTags,
    removeTag,
    clearTags,
    hasTagFilters,
    isTagSelected,
    toggleTag,
  } = useCatalogBrowseFilter();

  const { catalog: previewCatalog } = usePreviewStores();
  const catalog = props.catalogData?.catalog || previewCatalog || {};

  const allListenable = useMemo(
    () => getListenableCatalogItems(catalog),
    [catalog],
  );

  const featuredItems = useMemo(
    () => sortByReleaseNewest(allListenable.filter(isFeatured)),
    [allListenable],
  );

  const browsePool = useMemo(
    () => allListenable.filter((item) => !isFeatured(item)),
    [allListenable],
  );

  // Type options: prefer live item types; enrich from catalogMetadata when present
  const typeOptions = useMemo(() => {
    const fromItems = new Set(
      allListenable
        .map((i) =>
          String(i.type || "")
            .trim()
            .toLowerCase(),
        )
        .filter((t) => LISTENABLE_TYPES.includes(t)),
    );
    getMetadataValues(catalogMetadata, "type").forEach((t) => {
      const key = String(t).trim().toLowerCase();
      if (LISTENABLE_TYPES.includes(key)) fromItems.add(key);
    });
    return LISTENABLE_TYPES.filter((t) => fromItems.has(t));
  }, [allListenable, catalogMetadata]);

  // Tags present on non-featured catalog items (+ metadata)
  const vibeOptions = useMemo(() => {
    const byKey = new Map();
    const add = (raw) => {
      normalizeTags(raw).forEach((tag) => {
        const key = tag.toLowerCase();
        if (!key || byKey.has(key)) return;
        byKey.set(key, tag);
      });
    };
    browsePool.forEach((item) => add(item?.tags));
    getMetadataValues(catalogMetadata, "tags").forEach((t) => add(t));
    return Array.from(byKey.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [browsePool, catalogMetadata]);

  // Browse list: search + type + vibe tags (never touch featured)
  const filteredBrowse = useMemo(() => {
    const filtered = filterCatalogForBrowse(browsePool, {
      search,
      type: typeFilter,
      tags: selectedTags,
    });
    return sortCatalogItems(filtered, sortMode);
  }, [browsePool, search, typeFilter, selectedTags, sortMode]);

  // Featured rail is never filtered (search / type / vibe are catalog-only)
  const filteredFeatured = featuredItems;

  const playlistSlugClass =
    props.playlist && props.playlist.length > 0 && props.playlists?.[0]?.slug
      ? styles[props.playlists[0].slug]
      : "";

  const showBrowseControls = browsePool.length > 0;
  const showVibeFilters = vibeOptions.length > 0;
  const hasBrowseResults = filteredBrowse.length > 0;
  const hasAnyResults = filteredFeatured.length > 0 || hasBrowseResults;

  const vibeTriggerLabel = hasTagFilters
    ? `Vibe · ${selectedTags.length}`
    : "Vibe";

  useEffect(() => {
    if (!vibeMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (!vibeMenuRef.current) return;
      if (!vibeMenuRef.current.contains(e.target)) {
        setVibeMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setVibeMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [vibeMenuOpen]);

  return (
    <div
      id="music-catalog"
      className={
        styles["music-catalog-container"] +
        " " +
        styles["hasFeaturedElm-" + (filteredFeatured.length > 0)]
      }
    >
      <a name="music" href="#music" className={styles["anchor-page-bookmark"]}>
        Music Catalog page bookmark
      </a>
      <div className={styles["music-catalog"]}>
        {/* Shared hero: full-width flex row so underside matches Notice/About */}
        <div className={styles["section-hero-wrap"]}>
          <SectionHeroTitle
            className={styles["section-hero-full-width"]}
            title="Music Catalog"
            headingLevel={2}
            imageOpacity={0.125}
            imageOpacityMobile={0.33}
            imageOpacityUnderside={0.75} // lower plane (defaults to imageOpacity if omitted)
            imageOpacityUndersideMobile={0.33}
            imageOverlay="none" // "none" | "light" | "medium" | "dark" | "very-dark"
            showImageUnderside
          />
        </div>
        <div
          className={
            styles["service-selector-wrap"] +
            " " +
            styles[
              "hide-" +
                (welcomeScrollPosition &&
                  welcomeScrollPosition.hasOwnProperty("top") &&
                  welcomeScrollPosition.top >= -50)
            ]
          }
        >
          {!props.hideServiceSelector && (
            <div className={styles["service-selector-inner"]}>
              <ServiceSelector sourceURLObj={props.sourceURLObj} />
            </div>
          )}
        </div>

        {filteredFeatured.length > 0 && (
          <div className={styles["featured-container"]}>
            <ul
              className={
                styles["music-player-list"] + " " + styles["featured-list"]
              }
            >
              {filteredFeatured.map((entry) => (
                <CatalogPlayerCard
                  key={catalogItemAnchorId(entry) || entry.title}
                  entry={entry}
                  featured
                  playlistSlugClass={playlistSlugClass}
                  relatedItems={getRelatedItems(entry, allListenable, {
                    limit: 3,
                  })}
                />
              ))}
            </ul>
          </div>
        )}

        {/* Catalog list only; search / type / vibe / sort never filter Featured */}
        <div id="catalog-browse" className={styles["catalog-browse"]}>
          {showBrowseControls && (
            <div
              className={
                styles["catalog-toolbar"] +
                " " +
                styles["catalog-toolbar-browse"]
              }
            >
              <div className={styles["toolbar-row"]} role="search">
                <label
                  className={styles["search-label"]}
                  htmlFor="catalog-search"
                >
                  <span className={styles["visually-hidden"]}>
                    Search catalog by title or tags
                  </span>
                  <input
                    id="catalog-search"
                    type="search"
                    className={styles["search-input"]}
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoComplete="off"
                  />
                </label>

                {typeOptions.length > 1 && (
                  <label className={styles["control-field"]}>
                    <span className={styles["visually-hidden"]}>
                      Filter by type
                    </span>
                    <select
                      className={styles["control-select"]}
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      aria-label="Filter by type"
                    >
                      <option value="">All types</option>
                      {typeOptions.map((type) => (
                        <option key={type} value={type}>
                          {formatTypeLabel(type)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {showVibeFilters && (
                  <div className={styles["vibe-dropdown"]} ref={vibeMenuRef}>
                    <button
                      type="button"
                      className={
                        styles["control-select"] +
                        " " +
                        styles["vibe-trigger"] +
                        (hasTagFilters
                          ? " " + styles["vibe-trigger-active"]
                          : "") +
                        (vibeMenuOpen ? " " + styles["vibe-trigger-open"] : "")
                      }
                      aria-haspopup="listbox"
                      aria-expanded={vibeMenuOpen}
                      aria-label="Filter by vibe tags"
                      onClick={() => setVibeMenuOpen((open) => !open)}
                    >
                      <span className={styles["vibe-trigger-text"]}>
                        {vibeTriggerLabel}
                      </span>
                      <span
                        className={styles["control-caret"]}
                        aria-hidden="true"
                      >
                        ▾
                      </span>
                    </button>
                    {vibeMenuOpen && (
                      <div
                        className={styles["vibe-menu"]}
                        role="listbox"
                        aria-multiselectable="true"
                        aria-label="Vibe tags"
                      >
                        <div className={styles["vibe-menu-header"]}>
                          <span>Vibe tags</span>
                          {hasTagFilters && (
                            <button
                              type="button"
                              className={styles["vibe-menu-clear"]}
                              onClick={() => {
                                clearTags();
                              }}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <ul className={styles["vibe-menu-list"]}>
                          {vibeOptions.map((tag) => {
                            const active = isTagSelected(tag);
                            return (
                              <li key={tag}>
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={active}
                                  className={
                                    styles["vibe-menu-item"] +
                                    (active
                                      ? " " + styles["vibe-menu-item-active"]
                                      : "")
                                  }
                                  onClick={() => toggleTag(tag)}
                                >
                                  <span
                                    className={styles["vibe-menu-check"]}
                                    aria-hidden="true"
                                  >
                                    {active ? "✓" : ""}
                                  </span>
                                  <span className={styles["vibe-menu-label"]}>
                                    {tag}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <label className={styles["control-field"]}>
                  <span className={styles["visually-hidden"]}>
                    Sort catalog
                  </span>
                  <select
                    className={styles["control-select"]}
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value)}
                    aria-label="Sort catalog"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>

                {(search || typeFilter || hasTagFilters) && (
                  <p className={styles["toolbar-hint"]}>
                    {filteredBrowse.length} match
                    {filteredBrowse.length === 1 ? "" : "es"}
                  </p>
                )}
              </div>
            </div>
          )}

          {hasTagFilters && (
            <div
              className={styles["active-filters"]}
              role="status"
              aria-live="polite"
            >
              <div className={styles["active-filters-copy"]}>
                <p className={styles["active-filters-label"]}>
                  Filtering by vibe
                </p>
                <p className={styles["active-filters-hint"]}>
                  Showing releases that match at least one selected vibe.
                </p>
              </div>
              <ul className={styles["active-filter-chips"]}>
                {selectedTags.map((tag) => (
                  <li key={tag}>
                    <button
                      type="button"
                      className={styles["active-filter-chip"]}
                      onClick={() => removeTag(tag)}
                      aria-label={`Remove vibe filter ${tag}`}
                    >
                      <span className={styles["active-filter-chip-text"]}>
                        {tag}
                      </span>
                      <span
                        className={styles["active-filter-chip-x"]}
                        aria-hidden="true"
                      >
                        ×
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={styles["clear-filters-btn"]}
                onClick={clearTags}
              >
                Clear vibe filters
              </button>
            </div>
          )}

          {!hasBrowseResults && (search || typeFilter || hasTagFilters) && (
            <p className={styles["empty-state"]}>
              No catalog releases match
              {hasTagFilters
                ? ` those vibe tag${selectedTags.length === 1 ? "" : "s"}`
                : ""}
              {typeFilter ? ` in ${formatTypeLabel(typeFilter)}` : ""}
              {search ? ` for “${search}”` : ""}. Try clearing filters.
              {hasTagFilters && (
                <>
                  {" "}
                  <button
                    type="button"
                    className={styles["empty-clear-link"]}
                    onClick={clearTags}
                  >
                    Clear vibe filters
                  </button>
                </>
              )}
            </p>
          )}

          {!hasAnyResults && !search && !typeFilter && !hasTagFilters && (
            <p className={styles["empty-state"]}>
              No releases to show right now.
            </p>
          )}

          {hasBrowseResults && (
            <ul className={styles["music-player-list"]}>
              {filteredBrowse.map((entry) => (
                <CatalogPlayerCard
                  key={catalogItemAnchorId(entry) || entry.title}
                  entry={entry}
                  playlistSlugClass={playlistSlugClass}
                  relatedItems={getRelatedItems(entry, allListenable, {
                    limit: 3,
                  })}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MusicCatalog;
